import type { GrantDef, GrantPayloadLike } from "./GrantLoader";
import type { VarType } from "./Blackboard";

/** One entry in a game's declared `url_params` schema (req Module 12.3 —
 *  a platform-level URL launch parameter feature, not Prelude-specific).
 *  Structurally identical to PlatformAdapter's `CatalogueEntry.urlParams`
 *  entries, redeclared here so the engine doesn't import from platform/
 *  (same reasoning as GrantLoader's `GrantPayloadLike`). */
export interface UrlParamDef {
  name: string;
  var: string;
  type: string;
  allowed: string[];
  default: string;
}

function coerce(type: string, raw: string): unknown {
  switch (type) {
    case "int": {
      const n = parseInt(raw, 10);
      return Number.isNaN(n) ? raw : n;
    }
    case "float": {
      const n = parseFloat(raw);
      return Number.isNaN(n) ? raw : n;
    }
    case "bool":
      return raw === "true" || raw === "1";
    default:
      // str: passed through as-is. Ternary: passed through as-is too — the
      // raw string (e.g. an "on"/"off" allowed value) goes through
      // GrantLoader's own Ternary.from() normalization at merge time, same
      // as any other Ternary-typed grant.
      return raw;
  }
}

/** Declared url_params, reshaped as `GrantDef`s so they can be merged
 *  straight into the same declared-list `GrantLoader.from()` already takes
 *  for compiler grants — one `GrantLoader` per load, fed from both sources. */
export function urlParamsToGrantDefs(defs: UrlParamDef[]): GrantDef[] {
  return defs.map((d) => ({ id: d.var, type: d.type as VarType }));
}

/** Resolves each declared url_param against the current URL's query string:
 *  a present, `allowed`-listed value wins; anything else (absent, or present
 *  but not in `allowed`) silently falls back to the param's own declared
 *  `default` — per req §12.3, not the VAR's own compiled default (a
 *  different fallback than plain grants, which is why this always populates
 *  every declared var rather than leaving unset ones out of the payload). */
export function urlParamsToPayload(defs: UrlParamDef[], search: URLSearchParams): GrantPayloadLike {
  const grants: Record<string, unknown> = {};
  for (const d of defs) {
    const raw = search.get(d.name);
    const matched = raw !== null && (d.allowed.length === 0 || d.allowed.includes(raw)) ? raw : d.default;
    grants[d.var] = coerce(d.type, matched);
  }
  return { grants, expires: null };
}
